<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { fleetFilter } from '$lib/stores/selection';

  /**
   * Road transport licences, from the Federal Office of Transport's register:
   * which kinds of vehicle, any of them, and how many at least. A company's
   * branches count towards it.
   *
   * Switched on by the customer or by a selection arriving, and off only by
   * the customer, which clears it — the contract every filter in this panel
   * keeps. Ticking it on with nothing chosen yet filters nothing.
   */
  const kinds = ['heavy', 'light', 'passenger'];

  /** The least numbers of vehicles offered; 0 is any. */
  const minimums = [0, 5, 10, 20, 50, 100];

  let enabled = false;
  $: if ($fleetFilter && $fleetFilter.kinds.length > 0) enabled = true;

  $: chosen = $fleetFilter?.kinds ?? [];
  $: least = $fleetFilter?.min_vehicles ?? 0;

  function write(nextKinds: string[], nextLeast: number) {
    fleetFilter.set(
      nextKinds.length > 0
        ? { kinds: nextKinds, ...(nextLeast > 0 ? { min_vehicles: nextLeast } : {}) }
        : null
    );
  }

  function toggleEnabled() {
    enabled = !enabled;
    if (!enabled) fleetFilter.set(null);
  }

  function toggle(kind: string) {
    write(chosen.includes(kind) ? chosen.filter((k) => k !== kind) : [...chosen, kind], least);
  }

  const checkbox =
    'h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200';
</script>

<div class={enabled ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  <label class="group flex cursor-pointer items-center" title={$_('filters.fleet.explain')}>
    <input type="checkbox" checked={enabled} on:click={toggleEnabled} class="mr-2 {checkbox}" />
    <svg class="filter-mark mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M3 7h11v9H3zm11 3h4l3 3v3h-7M7 19a2 2 0 100-4 2 2 0 000 4zm10 0a2 2 0 100-4 2 2 0 000 4z"
      />
    </svg>
    <span class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700">
      {$_('filters.fleet.title')}
    </span>
  </label>

  {#if enabled}
    <div class="mt-3 space-y-2">
      <p class="text-xs text-gray-400">{$_('filters.fleet.anyOf')}</p>
      {#each kinds as kind (kind)}
        <label class="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            checked={chosen.includes(kind)}
            on:change={() => toggle(kind)}
            class="mt-0.5 {checkbox}"
          />
          <span class="text-sm text-gray-900">{$_(`filters.fleet.${kind}`)}</span>
        </label>
      {/each}
      <label class="flex items-center gap-2 pt-1 text-xs text-gray-500">
        {$_('filters.fleet.minimum')}
        <select
          class="rounded-control border border-hairline bg-surface-panel px-2 py-1 text-xs"
          value={least}
          disabled={chosen.length === 0}
          on:change={(e) => write(chosen, Number(e.currentTarget.value))}
        >
          {#each minimums as n (n)}
            <option value={n}>{n === 0 ? $_('filters.fleet.any') : n}</option>
          {/each}
        </select>
      </label>
    </div>
  {/if}
</div>
